
import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import LocationMap from './LocationMap';

export default function Settings({ isDarkMode, setIsDarkMode }) {
    const [restaurantName, setRestaurantName] = useState('');
    const [opHours, setOpHours] = useState({ open: '09:00', close: '22:00' });
    const [address, setAddress] = useState({ line1: '', city: '', zip: '' });
    const [location, setLocation] = useState({ lat: '', lng: '' });
    const [gst, setGst] = useState(0);
    const [packingCharges, setPackingCharges] = useState(0);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const styles = getStyles(isDarkMode);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const res = await api.get('/restaurant/settings');
            const data = res.data.settings; // Entire restaurant object

            if (data.name) setRestaurantName(data.name);
            if (data.operatingHours) setOpHours(data.operatingHours);
            if (data.address) setAddress(data.address);
            if (data.location) setLocation({
                lat: data.location.lat || '',
                lng: data.location.lng || ''
            });
            if (data.gstPercentage) setGst(data.gstPercentage);
            if (data.packingCharges) setPackingCharges(data.packingCharges);
            if (data.imageUrl) setLogoPreview(data.imageUrl);

        } catch (error) {
            console.error("Failed to fetch settings", error);
        }
        setLoading(false);
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleSaveSettings = async () => {
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('name', restaurantName);
            formData.append('gstPercentage', gst);
            formData.append('packingCharges', packingCharges);
            formData.append('operatingHours', JSON.stringify(opHours));
            formData.append('address', JSON.stringify(address));
            formData.append('location', JSON.stringify(location));

            if (logoFile) {
                formData.append('logo', logoFile);
            }

            await api.put('/restaurant/settings', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            alert("Settings Saved!");
        } catch (error) {
            console.error(error);
            alert("Error: " + (error.response?.data?.error || error.message));
        }
        setSaving(false);
    };

    const handleDetectLocation = () => {
        if (!navigator.geolocation) {
            alert('Geolocation is not supported by your browser');
            return;
        }

        setSaving(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const newLat = position.coords.latitude.toString();
                const newLng = position.coords.longitude.toString();
                setLocation({ lat: newLat, lng: newLng });
                setSaving(false);
                alert('Location detected! Click Save to persist.');
            },
            (error) => {
                console.error('Error detecting location:', error);
                alert('Unable to retrieve location.');
                setSaving(false);
            }
        );
    };

    if (loading) return <div style={styles.container}>Loading settings...</div>;

    return (
        <div style={styles.container}>
            <h2 style={styles.title}>Settings</h2>

            {/* Basic Info */}
            <div style={styles.section}>
                <h3>Restaurant Info</h3>

                {/* Logo Upload */}
                <div style={styles.formGroup}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                        <div style={styles.logoPreview}>
                            {logoPreview ? (
                                <img src={logoPreview} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                            ) : (
                                <span style={{ color: '#888' }}>No Logo</span>
                            )}
                        </div>
                        <input type="file" accept="image/*" onChange={handleLogoChange} style={{ fontSize: '0.9rem' }} />
                    </div>
                </div>

                <div style={styles.formGroup}>
                    <div style={styles.inputGroup}>
                        <label>Restaurant Name</label>
                        <input
                            value={restaurantName}
                            onChange={e => setRestaurantName(e.target.value)}
                            style={styles.input}
                        />
                    </div>
                </div>

                <div style={styles.formGroup}>
                    <div style={styles.inputGroup}>
                        <label>GST (%)</label>
                        <input
                            type="number"
                            value={gst}
                            onChange={e => setGst(e.target.value)}
                            style={styles.input}
                        />
                    </div>
                    <div style={styles.inputGroup}>
                        <label>Packing Charges (₹)</label>
                        <input
                            type="number"
                            value={packingCharges}
                            onChange={e => setPackingCharges(e.target.value)}
                            style={styles.input}
                        />
                    </div>
                </div>
            </div>

            <div style={styles.section}>
                <h3>Operating Hours</h3>
                <div style={styles.formGroup}>
                    <div style={styles.inputGroup}>
                        <label>Opening Time</label>
                        <input
                            type="time"
                            value={opHours.open}
                            onChange={e => setOpHours({ ...opHours, open: e.target.value })}
                            style={styles.input}
                        />
                    </div>
                    <div style={styles.inputGroup}>
                        <label>Closing Time</label>
                        <input
                            type="time"
                            value={opHours.close}
                            onChange={e => setOpHours({ ...opHours, close: e.target.value })}
                            style={styles.input}
                        />
                    </div>
                </div>
            </div>

            <div style={styles.section}>
                <h3>Location & Address</h3>
                <div style={styles.formGroup}>
                    <div style={styles.inputGroup}>
                        <label>Address Line 1</label>
                        <input
                            value={address.line1 || ''}
                            onChange={e => setAddress({ ...address, line1: e.target.value })}
                            style={styles.input}
                        />
                    </div>
                    <div style={styles.inputGroup}>
                        <label>City</label>
                        <input
                            value={address.city || ''}
                            onChange={e => setAddress({ ...address, city: e.target.value })}
                            style={styles.input}
                        />
                    </div>
                    <div style={styles.inputGroup}>
                        <label>Zip Code</label>
                        <input
                            value={address.zip || ''}
                            onChange={e => setAddress({ ...address, zip: e.target.value })}
                            style={styles.input}
                        />
                    </div>
                </div>

                <div style={styles.group}>
                    <label>Location Coordinates (Drag pin to adjust)</label>
                    <div style={{ marginBottom: '10px' }}>
                        <LocationMap
                            lat={location.lat}
                            lng={location.lng}
                            onChange={(lat, lng) => {
                                setLocation({ lat: lat.toString(), lng: lng.toString() });
                            }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>Latitude</label>
                            <input
                                value={location.lat}
                                onChange={e => setLocation({ ...location, lat: e.target.value })}
                                style={styles.input}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>Longitude</label>
                            <input
                                value={location.lng}
                                onChange={e => setLocation({ ...location, lng: e.target.value })}
                                style={styles.input}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={handleDetectLocation}
                            style={{ ...styles.detectBtn, height: '42px' }}
                            title="Detect current location"
                        >
                            📍 Detect
                        </button>
                    </div>
                </div>
            </div>

            <div style={styles.section}>
                <h3>Appearance</h3>
                <div style={styles.row}>
                    <span>Dark Mode</span>
                    <label style={styles.switch}>
                        <input
                            type="checkbox"
                            checked={isDarkMode}
                            onChange={(e) => setIsDarkMode(e.target.checked)}
                        />
                        <span style={styles.slider}></span>
                    </label>
                </div>
            </div>

            <button onClick={handleSaveSettings} disabled={saving} style={styles.saveBtn}>
                {saving ? 'Saving...' : 'Save Settings'}
            </button>
        </div>
    );
}

const getStyles = (isDark) => ({
    container: {
        padding: '30px',
        color: isDark ? '#fff' : '#333',
        maxWidth: '800px',
        paddingBottom: '100px'
    },
    title: {
        marginBottom: '30px',
        borderBottom: isDark ? '1px solid #444' : '1px solid #ddd',
        paddingBottom: '10px'
    },
    section: {
        marginBottom: '40px',
        backgroundColor: isDark ? '#2a2a2a' : '#fff',
        padding: '24px',
        borderRadius: '8px',
        border: isDark ? '1px solid #333' : '1px solid #eee',
        boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.05)'
    },
    row: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '15px'
    },
    formGroup: {
        display: 'flex',
        gap: '20px',
        marginTop: '15px',
        marginBottom: '20px',
        flexWrap: 'wrap'
    },
    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        flex: 1,
        minWidth: '200px'
    },
    input: {
        padding: '10px',
        borderRadius: '4px',
        border: isDark ? '1px solid #555' : '1px solid #ddd',
        backgroundColor: isDark ? '#333' : '#fff',
        color: isDark ? '#fff' : '#333'
    },
    saveBtn: {
        padding: '12px 24px',
        backgroundColor: '#0F9D58',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '1rem',
        fontWeight: 'bold',
        width: '100%',
        marginBottom: '20px'
    },
    detectBtn: {
        padding: '8px 16px',
        backgroundColor: '#6C5CE7',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '0.9rem',
        fontWeight: 'bold'
    },
    switch: {
        position: 'relative',
        display: 'inline-block',
        width: '48px',
        height: '24px'
    },
    slider: {
        position: 'absolute',
        cursor: 'pointer',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: isDark ? '#555' : '#ccc',
        transition: '.4s',
        borderRadius: '34px'
    },
    logoPreview: {
        width: '120px',
        height: '120px',
        borderRadius: '8px',
        border: isDark ? '2px dashed #444' : '2px dashed #ccc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        backgroundColor: isDark ? '#333' : '#f9f9f9'
    }
});
